/** Renderer-neutral highlight matching utility. */

export type HighlightSegment = { text: string; highlighted: boolean };

/**
 * Split text into segments based on highlight phrases.
 *
 * Matching rules:
 * 1. Deduplicate highlights case-insensitively
 * 2. Sort by longest match first (prevents partial collisions)
 * 3. All occurrences of each phrase are highlighted
 * 4. Non-overlapping — once matched, characters are consumed
 */
export function splitHighlights(text: string, highlights: string[]): HighlightSegment[] {
  if (!highlights.length) return [{ text, highlighted: false }];

  // Dedupe case-insensitively, keep original casing
  const unique = [...new Map(highlights.map((h) => [h.toLowerCase(), h])).values()];
  // Sort longest first to prevent partial collisions
  unique.sort((a, b) => b.length - a.length);

  const escaped = unique.map((h) => h.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const re = new RegExp(`(${escaped.join("|")})`, "gi");

  return text
    .split(re)
    .filter(Boolean)
    .map((part) => ({
      text: part,
      highlighted: unique.some((h) => h.toLowerCase() === part.toLowerCase()),
    }));
}
